import { Injectable, OnDestroy } from '@angular/core';

import { Observable, of, BehaviorSubject, forkJoin, EMPTY, iif, merge } from 'rxjs';
import { map, filter, tap, switchMap, take, catchError, shareReplay, mergeMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { DiscoverRoutingService } from '../../DiscoverRouter/services/discover-routing.service';
import { DiscoverRouteTypeEnum } from '../../DiscoverRouter/enums/discover-route-type.enum';
import { TargetingListsApiService, wsReloadListRequests } from '../../../api/targeting/targeting-lists.api.service';
import { TargetingListList } from '../models/targeting-list-list.model';
import { TargetingList } from '../models/targeting-list.model';
import { TargetingResultModel } from '../models/targeting-result.model';
import { TargetingSearchService } from './targeting-search.service';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { TargetingQueryApi } from '../../../api/targeting/model/targeting-search.api.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { EntityIdApiModel } from '../../../api/targeting/model/entity-id-api-model';
import { ListMembershipApiModel } from '../../../api/targeting/model/list-membership-api-model';
import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { FilterListProviderServiceInterface } from '../../../Common/Filter/services/filter-list-provider-service.interface';
import { TargetingSearch } from '../models/targeting-search.model';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { CurrentResourceAbstractService } from '../../../Common/Basic/service/current-resource-abstract.service';
import { ResourceAction } from '../../../Common/Basic/service/resource-abstract.service';
import { MultiBaseAbstractApi } from '@api/genericApi/multi-base-abstract.api';
import { TargetingListApi } from '@api/targeting/model/targeting-list-api.model';
import { TargetingListListApi } from '@api/targeting/model/targeting-list-list-api.model';
import { ResourceActionTypes } from '@Common/Basic/enums/resource-actions.enum';
import { CommonApiErrorEnum } from '@api/genericApi/enums/common-api-error.enum';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { DisplayGroupsService } from '@Common/Layout/services/display-groups.service';


@Injectable()
export class TargetingListService extends CurrentResourceAbstractService<TargetingList, TargetingListApi, TargetingListList, TargetingListListApi>
    implements OnDestroy, FilterListProviderServiceInterface {

    all$: Observable<TargetingListList[]> = this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.advancedTargeting]).pipe(
        switchMap((hasPermission) =>
            hasPermission ? merge(
                this.getApiService().getList().pipe(
                    map(list => list.map(item => this.constructListModel(item)))
                ),
                this.refreshAllManualSubject
            ) : of([])
        ),
        tap(lists => {
            this.targetingSearchService.addListsToFilters(lists);
        }),
        shareReplay(1)
    );

    private listMembershipsChanged$ = new BehaviorSubject<string|number>(null);

    constructor(
        private discoverRoutingService: DiscoverRoutingService,
        private targetingListsApiService: TargetingListsApiService,
        private messageService: MessageService,
        private displayGroupsService: DisplayGroupsService,
        private targetingSearchService: TargetingSearchService,
        private websocketEvents: WebsocketAwareNeptuneService,
        private currentUserStateService: CurrentUserStateService,
    ) {
        super();
        this.subscriptions.push(
            this.discoverRoutingService.getCurrentRoute().pipe(
                mergeMap((route) => {
                    if (route.type === DiscoverRouteTypeEnum.targetingListSearchResult) {
                        return this.setCurrentResourceById(+route.value)
                        .pipe(
                            tap(() => {
                                const search = TargetingSearch.constructFromRouteParam(route?.queryParamMap);
                                if (search) {
                                    this.targetingSearchService.applyInitFiltersOnListReady(search);
                                }
                            })
                        );
                    } else {
                        this.setCurrentResource(null);
                        return of(null);
                    }
                })
            ).subscribe(),
            this.websocketEvents.getWebsocketMessages().pipe(
                filter(x => !!x),
                switchMap(message => {
                    if ((message.resourceType === ResourceType.targetingLists ||
                        message.resourceType === ResourceType.distributions) &&
                        wsReloadListRequests.some(request => request.test(message.slug))
                    ) {
                        return this.refreshAllManual().pipe(
                            filter(x => !!x),
                            take(1),
                        );
                    }

                    return of(null);
                })
            ).subscribe(),
        );
    }

    getApiService(): MultiBaseAbstractApi<TargetingListApi, TargetingListListApi> {
        return this.targetingListsApiService;
    }

    constructResourceModel(apiModel: TargetingListApi): TargetingList {
        return new TargetingList(apiModel);
    }

    constructListModel(apiModel: TargetingListListApi): TargetingListList {
        return new TargetingListList(apiModel);
    }

    getFilterListItems(): Observable<AuditableModel[]> {
        return this.all$;
    }

    private setCurrentResourceById(id: number): Observable<null> {
        if (id === null) {
            this.setCurrentResource(null);
            return of(null);
        }

        return this.currentResource$.pipe(
            take(1),
            switchMap(focusedList => {
                if (!focusedList || focusedList.id !== id) {
                    return this.getById(id);
                } else {
                    this.targetingSearchService.performListSearch(focusedList);
                    return EMPTY;
                }
            }),
            catchError(error => {
                if (error.status === 404) {
                    const io = {
                        inputs: {
                            heading:'Monitor.results.targetedListNotFoundDialog.heading',
                            message:'Monitor.results.targetedListNotFoundDialog.message',
                            okLabel:'Monitor.results.targetedListNotFoundDialog.acknowledge',
                            showCancel:false,
                            confirmAction: () => {
                                this.discoverRoutingService.gotoListHype();
                                return of(true);
                            }

                        }
                    };

                    this.coreDialogService.open(ConfirmDialogComponent, io);
                }
                throw error;
            }),
            tap((resource) => {
                this.setCurrentResource(resource);
                this.targetingSearchService.performListSearch(resource);
            }),
            map(() => null)
        );
    }

    getListCount(listId: number): Observable<number> {
        return this.targetingListsApiService.getById(listId, true).pipe(
            map(list => list.count)
        );
    }


    create(list: TargetingList, group?: DisplayGroupModel): Observable<number> {
        return this.createResource(list).pipe(
            switchMap(
                (listId) => {
                    return this.refreshAllManual().pipe(
                        filter(x => !!x),
                        take(1),
                        map(x => listId)
                    );
                }
            ),
            switchMap((id) => {
                if (group) {
                    return this.updateGroup([id], group).pipe(
                        map(x => id)
                    );
                } else {
                    return of(id);
                }
            }),
        );
    }


    /**
     * Delete the specified listId and force refresh of all$ before returning listId.
     * If the currentResource is the list being deleted, nav to hype screen & reset currentResource to null.
     * @param listId - TargetingList id that is being deleted
     */
    delete(listId: number): Observable<number> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((focusedList) => {
                return this.deleteResource(listId, {
                    actionType: ResourceActionTypes.DELETE,
                    resetResource: focusedList?.id === listId
                }).pipe(
                    tap(() => {
                        if (focusedList?.id === listId) {
                            this.discoverRoutingService.gotoListHype();
                        }
                    })
                );
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            }),
        );
    }

    /** Check for duplicates, then rename the specified targeting list */
    renameListDupCheck(id: number, name: string): Observable<number> {
        return this.isNameDuplicated$(name, id).pipe(
            switchMap((duplicate) => {
                if (duplicate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.targeting.alerts.renameListError.summary',
                        detail: 'Discover.targeting.alerts.renameListError.duplicate.detail'
                    });
                    throw new Error(CommonApiErrorEnum.duplicateName);
                } else {
                    return this.rename(id, name);
                }
            })
        );
    }

    /**
     * Rename the specified list (via id) with the new name and force refresh of all$ before returning listId.
     * If the currentResource is the list being renamed, force a refresh of the currentResource.
     * @param listId - TargetingList id that is being renamed
     * @param name - the new updated name property
     * @param refreshResource - should be true when currentResource is the one being renamed
     */
    rename(listId: number, name: string): Observable<number> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((focusedList) => {
                return this.getById(listId).pipe(
                    switchMap((list) => {
                        list.name = name;
                        return this.updateResource(list, {
                            actionType: ResourceActionTypes.RENAME,
                            refreshResource: focusedList?.id === listId,
                        });
                    }),
                    switchMap((id) => {
                        return this.refreshAllManual().pipe(
                            filter(x => !!x),
                            take(1),
                            map(x => id)
                        );
                    })
                );
            })
        );
    }

    updatePortfolio(list: TargetingList): Observable<number> {
        return this.targetingListsApiService.update(list.toApiModel()).pipe(
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.portfolios.error.summary',
                });

                throw new Error(error);
            }),
            tap(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.portfolios.success.summary',
                });
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }


    /** Update the hidden state of the specified list (via id) and force refresh of all$ before returning listId. */
    updateVisibility(listId: number, action: ActionsEnum): Observable<number> {
        return this.getById(listId).pipe(
            switchMap((list) => {
                list.hidden = action === ActionsEnum.hide;
                return this.updateResource(list, { actionType: ResourceActionTypes.UPDATE }, false);
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }

    /** Update the hidden state of the specified lists and force refresh of all$ before returning list ids. */
    bulkUpdateVisibility(listIds: number[], action: ActionsEnum): Observable<number[]> {
        return forkJoin(listIds.map(id => this.getById(id))).pipe(
            switchMap((lists: TargetingList[]) => {
                lists.forEach(list => list.hidden = action === ActionsEnum.hide);
                return this.updateBulkResources(lists);
            }),
            switchMap((ids) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => ids)
                );
            })
        );
    }

    /**
     * Similar logic is used across different services.
     *
     * @TODO Consider consolidating repeated bulk deletion logic using shared CRUD interfaces.
     */
    confirmBulkDelete(items: AuditableModel[], groups: DisplayGroupModel[]): Observable<boolean> {
        const list = items.map(item => item.name);
        const secondaryList = groups.map(item => item.name);

        return new Observable<boolean>((subscriber) => {
            let isListSelected: boolean = true;
            let isSecondaryListSelected: boolean = false;

            const confirmConfig: ConfirmDialogModel = {
                inputs: {
                    id: 'bulkDeleteTargetedLists',
                    heading: 'Discover.confirmDialog.bulkDeleteList.heading',
                    message: 'Common.confirmDialog.message',
                    list,
                    listLabel: 'Discover.confirmDialog.bulkDeleteList.checkboxLabel',
                    isListSelected,
                    secondaryList,
                    secondaryListLabel: 'Common.confirmDialog.checkboxLabel.groups',
                    isSecondaryListSelected,
                    okLabel: 'Tags.manageDialog.delete'
                },
                outputs: {
                    listSelectionChanged: (isSelected: boolean) => {
                        isListSelected = isSelected;
                    },
                    secondaryListSelectionChanged: (isSelected: boolean) => {
                        isSecondaryListSelected = isSelected;
                    },
                    dialogClose: (confirmed: any) => {
                        if (confirmed) {
                            if (isListSelected) {
                                const ids = items.map(item => item.id);
                                this.bulkDelete(ids).subscribe();
                            }

                            if (isSecondaryListSelected) {
                                groups.forEach(group => this.displayGroupsService.deleteGroup(group));
                            }
                        }
                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
        });
    }
    private bulkDelete(ids: number[]): Observable<number[]> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((focusedList) => {
                const action = {
                    actionType: ResourceActionTypes.BULK_DELETE,
                    resetResource: ids.includes(focusedList?.id),
                    count: ids.length
                };

                return this.bulkDeleteResourceByIds(ids, action).pipe(
                    tap(() => {
                        if (ids.includes(focusedList?.id)) {
                            this.discoverRoutingService.gotoListHype();
                        }
                    })
                );
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }

    copyTargetingList(list: TargetingListList) {
        const io: NewResourceDialogModel = {
            inputs: {
                heading: 'Discover.CopyTargetedList',
                initialValue: list.name + this.localizationService.transform('Common.dialog.newResource.copy.suffix'),
                currentGroup: list.groupId,
                okLabel: 'Common.dialog.newResource.copy',
                observableFormValidator: (listName: string) => {
                    return this.isNameDuplicated$(listName).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['Discover.dialogs.newList.duplicate'];
                            } else {
                                return [];
                            }
                        })
                    );
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    let newId = null;

                    const newList = new TargetingList({name: name, displayGroupId: group?.id});
                    return this.createResource(newList, { actionType: ResourceActionTypes.COPY }).pipe(
                        switchMap(id => {
                            newId = id;
                            const query = new TargetingSearch();
                            query.list.include = [list.id];
                            return this.addSearchResultsToList([id], query.toApiModel().query);
                        }),
                        tap(() => this.discoverRoutingService.gotoListSearchResults(newId)),
                        switchMap(
                            () => {
                                return this.refreshAllManual().pipe(
                                    filter(x => !!x),
                                    take(1),
                                );
                            }
                        ),
                    );
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, io);
    }


    /**
     * Adds or removes Targeting Lists to/from a group
     * @param listIds - Targeting Lists Ids
     * @param group - DisplayGroup (property id can be undefined in the case of Uncategorized)
     * @param refreshAll - boolean default false
     */
    updateGroup(listIds: number[], group: DisplayGroupModel, refreshAll: boolean = false): Observable<TargetingList> {
        let request: Observable<void>;

        if (group.id) {
            request = this.targetingListsApiService.updateListDisplayGroup(listIds, [], group.id);
        } else {
            request = this.targetingListsApiService.removeListsFromDisplayGroup(listIds);
        }

        return request.pipe(
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: 'Common.lhp.moveToGroup.error.detail'
                });

                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1)
                );
            }),
            switchMap(() =>
                iif(
                    () => refreshAll,
                    this.refreshAllManual().pipe(
                        filter(x => !!x),
                        take(1),
                    ),
                    of(null)
                )
            ),
            switchMap(() =>
                iif(
                    () => listIds.length === 1,
                    this.getById(listIds[0]),
                    of(null)
                )
            ),
            tap((list: TargetingList) => {
                if (list) {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.lhp.moveToGroup.success.summary',
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.single',
                            { itemName: list?.name, groupName: group.name }
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.lhp.moveToGroup.success.summary',
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.multiple',
                            { number: listIds.length, name: group.name }
                        )
                    });
                }
            })
        );
    }

    removeEntitiesFromList(
        entities?: TargetingResultModel[],
        removeEntitiesMatchingSearch?: TargetingQueryApi,
        total?: number
    ): Observable<TargetingList> {

        return this.currentResource$.pipe(
            take(1),
            switchMap((focusedList) => {
                let apiCall: Observable<void>;
                if (!!entities) {
                    apiCall = this.targetingListsApiService.removeEntitiesFromList(focusedList?.id, entities.map(x => x.toEntityIdModel()));
                } else {
                    apiCall = this.targetingListsApiService.removeEntitiesMatchingSearch(focusedList?.id, removeEntitiesMatchingSearch);
                }

                return apiCall.pipe(
                    catchError((error) => {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Discover.targeting.alerts.removeFromListError.summary'
                        });

                        throw new Error(error);
                    }),
                    switchMap((x) => {
                        return this.getById(focusedList?.id);
                    })
                );
            }),
            tap((resource) => {
                this.setCurrentResource(resource);
                this.targetingSearchService.doSearch(true, true);

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.targeting.alerts.removeFromListSuccess.summary',
                    detail: this.localizationService.transform('Discover.targeting.alerts.removeFromListSuccess.detail', {
                        count: entities ? entities.length : total,
                        listName: resource.name
                    })
                });
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }

    removeEntitiesFromDist(listIds: number[], entities: TargetingResultModel[], tooltipNames: string): Observable<number[]> {
        const apiCalls: Observable<void>[] = listIds.map(id => this.targetingListsApiService.removeEntitiesFromList(
            id,
            entities.map(x => x.toEntityIdModel())
        )) || [];

        return forkJoin(apiCalls).pipe(
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.targeting.alerts.removeFromDistError.summary'
                });

                throw new Error(error);
            }),
            tap(() => {
                this.targetingSearchService.doSearch(true, true);
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.targeting.alerts.removeFromDistSuccess.summary',
                    detail: this.localizationService.transform('Discover.targeting.alerts.removeFromDistSuccess.detail', {
                        count: entities?.length,
                        distName: tooltipNames
                    })
                });
            }),
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => listIds)
                );
            })
        );
    }


    /**
     * Removes the provided lists from all display groups & refresh all$
     */
    removeListsFromGroup(listsIds: number[]): Observable<null> {
        return this.targetingListsApiService.removeListsFromDisplayGroup(listsIds).pipe(
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            })
        );
    }

    updateFavorites(addIds: number[], removeIds: number[]): Observable<void> {
        return this.targetingListsApiService.updateFavorites(addIds, removeIds).pipe(
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: 'Common.lhp.moveToGroup.error.detail'
                });

                throw new Error(error);
            }),
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            }),
        );
    }

    getListMembershipsChanged(): Observable<any> {
        return this.listMembershipsChanged$;
    }

    listMembeshipsChanged(id: string | number): void {
        this.listMembershipsChanged$.next(id);
    }

    getListMemberships(entityIds: EntityIdApiModel[]): Observable<ListMembershipApiModel[]> {
        return this.targetingListsApiService.getListMemberships(entityIds);
    }

    updateListContactEmployment(listId: number, contact: TargetingResultModel): Observable<void> {
        return this.targetingListsApiService.updateListContactEmployment(listId, contact.id, contact.selectedEmployment.id).pipe(
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.targeting.result.contactChangeOutlet.header',
                    detail: this.localizationService.transform('Discover.targeting.result.contactChangeOutlet.message.error', {
                        contactName: contact.contactName
                    })
                });

                throw new Error(error);
            }),
            tap(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.targeting.result.contactChangeOutlet.header',
                    detail: this.localizationService.transform('Discover.targeting.result.contactChangeOutlet.message', {
                        contactName: contact.contactName
                    })
                });
            })
        );
    }

    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as TargetingList;

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.targeting.alerts.createListError.summary',
                        detail: this.localizationService.transform('Discover.targeting.alerts.createListError.detail', {
                            listName: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.targeting.alerts.createListSuccess.summary',
                        detail: this.localizationService.transform('Discover.targeting.alerts.createListSuccess.detail', {
                            listName: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.confirmDialog.deleteList.error',
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.confirmDialog.deleteList.success',
                    });
                }

                break;
            }
            case ResourceActionTypes.BULK_DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.confirmDialog.bulkDeleteList.error.summary',
                        detail: 'Discover.confirmDialog.bulkDeleteList.error.detail'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.confirmDialog.bulkDeleteList.success.summary',
                        detail: this.localizationService.transform('Discover.confirmDialog.bulkDeleteList.success.detail', {
                            number: actionResult.count
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.RENAME: {
                if (error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Discover.targeting.alerts.renameListError.summary',
                        detail: error.error || 'Discover.targeting.alerts.renameListError.detail'
                    });
                } else {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary: 'Discover.targeting.alerts.renameListSuccess.summary',
                        detail: this.localizationService.transform('Discover.targeting.alerts.renameListSuccess.detail', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.COPY: {
                if (error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Discover.CopyTargetedList',
                        detail: 'Discover.confirmDialog.copy.error'
                    });
                } else {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary: 'Discover.CopyTargetedList',
                        detail: 'Discover.confirmDialog.copy.success'
                    });
                }

                break;
            }
            case ResourceActionTypes.BULK_VISIBILITY: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.targeting.alerts.bulkUpdateError.summary',
                        detail: 'Discover.targeting.alerts.bulkUpdateError.detail'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Discover.targeting.alerts.bulkUpdateSuccess.summary',
                        detail: this.localizationService.transform('Discover.targeting.alerts.bulkUpdateSuccess.detail', {
                            count: actionResult.count
                        })
                    });
                }

                break;
            }
        }
    }

    addEntitiesToLists(listIds: number[], entityIds: EntityIdApiModel[]): Observable<void> {
        return this.targetingListsApiService.addEntitiesToList(listIds, entityIds).pipe(
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            }),
        );
    }

    addSearchResultsToList(ids: number[], targetingQuery: TargetingQueryApi): Observable<void> {
        return this.targetingListsApiService.addSearchResultsToList(ids, targetingQuery).pipe(
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            }),
        );
    }

    removeEntitiesFromLists(ids: number[], entityIds: EntityIdApiModel[]): Observable<any> {
        const apiCalls: Observable<void>[] = [];
        ids.forEach(id => {
            apiCalls.push(this.targetingListsApiService.removeEntitiesFromList(id, entityIds));
        });
        return forkJoin(apiCalls).pipe(
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            }),
        );
    }

    removeSearchResultFromLists(ids: number[], query: TargetingQueryApi): Observable<any> {
        const apiCalls: Observable<void>[] = [];
        ids.forEach(id => {
            apiCalls.push(this.targetingListsApiService.removeEntitiesMatchingSearch(id, query));
        });
        return forkJoin(apiCalls).pipe(
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
